import { mergeAttributes } from "@tiptap/core";
import { TableHeader } from "@tiptap/extension-table-header";
import { Plugin } from "@tiptap/pm/state";
import { tableCellStyle } from "./table-cell";

/**
 * Finds the ranges a transaction actually inserted content into, mapped forward to `doc`'s
 * coordinates. Deliberately looks only at what's new, not at a cell's pre-existing content — so it
 * can never re-impose a mark a user has just removed (via the Bold button, or by undoing), which a
 * blanket "re-bold anything not fully bold" sweep on every transaction would.
 * @param {import("@tiptap/pm/state").Transaction} transaction the transaction to inspect
 * @param {import("@tiptap/pm/model").Node} doc the resulting document to map positions against
 * @returns {Array<{from: number, to: number}>} inserted content ranges, in `doc`'s coordinates
 */
function insertedRanges(transaction, doc) {
  const ranges = [];

  transaction.mapping.maps.forEach((stepMap, mapIndex) => {
    stepMap.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
      if (newEnd === newStart) {
        return;
      }

      const remainingMapping = transaction.mapping.slice(mapIndex + 1);
      ranges.push({
        from: remainingMapping.map(newStart, -1),
        to: remainingMapping.map(newEnd, 1),
      });
    });
  });

  return ranges.filter(({ from, to }) => from >= 0 && to <= doc.content.size);
}

export default TableHeader.extend({
  renderHTML({ HTMLAttributes }) {
    return [
      "th",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { style: tableCellStyle }),
      0,
    ];
  },
  /**
   * A browser (and FOX) renders a bare `<th>` bold by default, with or without any styling, so a
   * header cell whose text isn't a genuine bold mark still looks bold everywhere except inside this
   * editor. Bolding newly inserted content here — rather than only when a cell is explicitly
   * toggled to a header — covers typing into an empty header cell too (e.g. from "Create table"'s
   * default header row), not just the toggle buttons. Crucially, it only ever touches content a
   * transaction just inserted, never pre-existing content, so a user removing bold from header text
   * (via the Bold button, or by undoing) is never immediately fought and re-applied.
   * @returns {Array} ProseMirror plugins for this extension
   */
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (transactions, _oldState, newState) => {
          const boldMarkType = newState.schema.marks.bold;
          const headerType = newState.schema.nodes[this.name];
          const { tr } = newState;
          let changed = false;

          transactions.forEach((transaction) => {
            if (!transaction.docChanged) {
              return;
            }

            insertedRanges(transaction, newState.doc).forEach(({ from, to }) => {
              newState.doc.nodesBetween(from, to, (node, pos) => {
                if (node.type !== headerType) {
                  return true;
                }

                const rangeFrom = Math.max(from, pos + 1);
                const rangeTo = Math.min(to, pos + node.nodeSize - 1);

                if (rangeTo > rangeFrom) {
                  tr.addMark(rangeFrom, rangeTo, boldMarkType.create());
                  changed = true;
                }

                return false;
              });
            });
          });

          return changed ? tr : null;
        },
      }),
    ];
  },
});
